import { Mass } from "@/helpers/Mass";
import { Money } from "@/helpers/Money";
import { check } from "@/helpers/utils";
import { CartAddLineItem } from "@/services/shopifyCart";

type DroppsBaseVariant = {
  /** product variant is considered available for sale on shopify. */
  available: boolean;
  backorderEnabled: boolean;
  compareAtPrice?: Money;
  hidden: boolean;
  id: number;
  inventoryQuantity?: number;
  price: Money;
  requiresShipping: boolean;
  size: {
    value: string;
    unit?: string;
  };
  package_quantity: number;
  default_variant_multiplier: number;
  quantity_discount?: number;
  sku?: string;
  taxable?: boolean;

  /** Title of variant (eg: "Standard / Starter Kit") */
  title: string;
  weight: Mass;

  /**
   * See {@link variantSelect.orderableSubscription} to know if this
   * subscription can be ordered
   */
  subscription?: DroppsSubscription;
  metafields?: any;
};

export type DroppsVariant = {
  // kind: "standard";
} & DroppsBaseVariant;

export type DroppsDiscountFormula =
  | {
      kind: "percentage";
      percentage: number;
    }
  | {
      kind: "fixed";
      offset: Money;
    }
  | {
      kind: "price";
      price: Money;
    };

export type DroppsDiscount =
  | {
      kind: "percentage";
      percentage: number;
      price: Money;
    }
  | {
      kind: "fixed";
      offset: Money;
      price: Money;
    }
  | {
      kind: "price";
      price: Money;
    };

export type DroppsSubscription =
  | {
      kind: "variant";
      variantId: number;
      discount: DroppsDiscount;
      hidden: boolean;
    }
  | {
      kind: "selling-plan";
      discount: DroppsDiscount;
      hidden: boolean;
    };

export type DroppsProduct = {
  /** Small amount of text that can be badged onto the product image */
  badge?: string;

  /** Items that are also included with a product */
  bundledItems?: DroppsProduct[];

  /** A user quote about the product */
  featuredQuote?: string;

  /** A text pseudo-id like "stain-odor-laundry-detergent-pods-ocean" */
  handle: string;

  /** If a product should not be displayed or be orderable */
  hidden: boolean;

  /** If the subscriptions should be hidden on the front-end, but still processed by recharge*/
  hideSubscription: boolean;

  /** section to replace the visuals for future shipments on PDP pages */
  overrideFutureShipments?: string;

  id: number;

  /** List of videos and images for a product */
  media: ShopifyMedia[];

  /** List of Shopify */
  tags: string[];

  /**
   * The full name of a product, like "Ultra Concentrated Laundry Detergent
   * Starter Kit Stain & Odor, Lavender Eucalyptus"
   *
   * {@link productSelect.scent}
   * {@link productSelect.name}
   */
  title: string;

  /** The raw product description */
  descriptionHtml: string;

  /** Product Short Text metafield value {@link productSelect.highlight} */
  productShortText?: string;

  /** The category of product: "Kitchen" | "Laundry" | "Laundry & Household" */
  type: string;

  /** Product selling plans. May be empty as not all our products are subscribeable */
  sellingPlans: DroppsSellingPlan[];
  variants: DroppsVariant[];

  relatedProducts?: string[];
  pdpIngredientSection?: PdpIngredientSection | undefined;
  pdpTabData?: PdpTabData | undefined;
  variantMetadata?: Map<number, number>;
  scent?: ScentDetails;
  productGroup?: string;
  productCardDescription?: string;
};

export type VideoSource = {
  format: string;
  videoUrl: string;
  mimeType: string;
  height: number;
  width: number;
};

export type MediaImage = {
  url: string;
  alt: string | null;
  width: number;
  height: number;
};

export type MediaVideo = {
  url: string;
  alt: string | null;
  width: number;
  height: number;
  sources: VideoSource[];
};

export type ShopifyMediaImage = Extract<ShopifyMedia, { mediaType: "image" }>;

export type ShopifyMedia =
  | (MediaImage & { mediaType: "image" })
  | (MediaVideo & { mediaType: "video" });

// An alias for now, but can be expanded upon when we want to introduce
// additional fields (ie: cadence)
export type DroppsSellingPlan = ShopifySellingPlan;

export type BundledItems = {
  bundledSubscriptionProducts: ShopifyProduct[];
};

export type ProductRating = {
  reviewAverage: number;
  reviewCount: number;
};

export type IngredientSectionDrawerData = {
  ingredientDisclosureLink?: string;
  excludedIngredients?: {
    heading: string;
    ingredients: string[];
  };
  highlightedFeatures?: {
    heading: string;
    features: string[];
  };
  ingredients: {
    heading: string;
    ingredients: {
      name: string;
      type: string;
    }[];
  };
};

export type PdpIngredientSection = {
  ingredientDisclosureUrl?: string;
  ingredientSectionDrawerData?: IngredientSectionDrawerData;
};

export type PdpTabData = {
  overview?: string;
  impact?: string;
  ingredients?: string;
};
export type PdpFragranceSectionData = {
  title?: string;
  sectionBankgroundColor?: string;
  scentChipBg?: string;
  benefits?: string[];
  belowBenefitCopy?: string;
};
type MetaobjectFieldGql = {
  key: string;
  value: string;
};

type MetaobjectGql = {
  type: string;
  fields: MetaobjectFieldGql[];
};
export type ScentDetails = {
  title: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  handle: string;
  scentIngredientBackgroundColor?: string;
  ingredients?: ProductIngredients;
  hidden: boolean;
  pdpFragranceSectionContent: PdpFragranceSectionData | null;
  pdpFragranceSectionImage: string | null;
  isFragnanceSectionEnable: boolean;
};

export type UnknownProductIngredients = {
  title: undefined;
  contents: undefined;
  disclosureUrl: undefined;
};

export type KnownProductIngredients = {
  title: string;
  contents: string;
  disclosureUrl?: string;
};

export type ProductIngredients =
  | UnknownProductIngredients
  | KnownProductIngredients;

export type ImageAttributes = {
  src: string;
  alt?: string;
  height?: number;
  width?: number;
  loading?: "eager" | "lazy";
};

export function droppsCatalog(products: ShopifyProduct[]): DroppsCatalog {
  return {
    all: products.map(droppsProduct),
  };
}

/** Return a discount computation when applied at a given price  */
function applySubscriptionDiscount(
  discount: DroppsDiscountFormula,
  price: Money
): DroppsDiscount {
  switch (discount.kind) {
    case "price":
      return discount;
    case "fixed":
      return {
        kind: "fixed",
        offset: discount.offset,
        price: price.sub(discount.offset),
      };
    case "percentage":
      return {
        kind: "percentage",
        percentage: discount.percentage,
        price: price.mul(1 - discount.percentage / 100),
      };
  }
}

/** Multiply a discount by the given factor */
function discountFactor(
  discount: DroppsDiscount,
  factor: number
): DroppsDiscount {
  switch (discount.kind) {
    case "price":
      return {
        kind: "price",
        price: discount.price.mul(factor),
      };
    case "fixed":
      return {
        kind: "fixed",
        offset: discount.offset.mul(factor),
        price: discount.price.mul(factor),
      };
    case "percentage":
      return {
        kind: "percentage",
        percentage: discount.percentage,
        price: discount.price.mul(factor),
      };
  }
}

function parseSize(sizeValue: string): {
  value: string;
  unit: string | undefined;
} {
  //For refills - sizeValue is different. Ex: 6 Refills. Spliting it with space then reverse.
  const sizeArr = sizeValue.includes("Refills")
    ? sizeValue.split(" ").reverse()
    : sizeValue.split("/");
  const size = (
    sizeArr.length > 1 ? parseFloat(sizeArr[1]).toString() : sizeArr[0]
  ).trim();
  const sizeName = sizeArr.length === 1 ? undefined : sizeArr[0].trim();
  return { value: size, unit: sizeName };
}

export function getSizeInfo(options: string[]) {
  if (options.length > 0) {
    return parseSize(options[1] ?? options[0]);
  }
  return { value: "1", unit: "count" };
}

/** Map intermediate variant into dropps variant */
function mapVariant(
  v: ShopifyProductVariant,
  subscription?: DroppsSubscription
): DroppsVariant {
  const size = getSizeInfo(v.options);
  return {
    // kind: "standard",
    id: v.id,
    title: v.title,
    size,
    sku: v.sku ?? undefined,
    inventoryQuantity: v.inventory_quantity,
    requiresShipping: v.requires_shipping,
    backorderEnabled: v.inventory_policy === "continue",
    taxable: v.taxable,
    available: v.available,
    price: v.price,
    weight: v.weight,
    compareAtPrice: v.compare_at_price ?? undefined,
    hidden: v.hidden,
    subscription,
    package_quantity: v.package_quantity,
    default_variant_multiplier: v.variant_multiplier,
    quantity_discount: v.quantity_discount,
  };
}

export function droppsProduct({
  variants: rawVariants,
  bundledItems,
  ...product
}: ShopifyProduct): DroppsProduct {
  // All our selling plans SHOULD have the same discount, so take first
  const sellingPlan = product.sellingPlans[0];
  // Directly map over rawVariants without grouping
  const variants = rawVariants.map((variant) => {
    let subscription: DroppsSubscription | undefined = undefined;
    if (sellingPlan) {
      const option = variant.options[0].toLowerCase();
      if (["wash plan", "subscription"].some((x) => option.includes(x))) {
        subscription = {
          kind: "variant",
          variantId: variant.id,
          hidden: variant.hidden || product.hideSubscription,
          discount: {
            kind: "price",
            price: variant.price,
          },
        };
      } else {
        subscription = {
          kind: "selling-plan",
          hidden: product.hideSubscription,
          discount: applySubscriptionDiscount(
            sellingPlan.discount,
            variant.price
          ),
        };
      }
    }
    return mapVariant(variant, subscription);
  });

  // Sort variants by price
  variants.sort((a, b) => a.price.asCents() - b.price.asCents());

  return {
    ...product,
    bundledItems: bundledItems
      ? bundledItems.bundledSubscriptionProducts.map(droppsProduct)
      : undefined,
    variants,
  };
}

export type DroppsCatalog = {
  all: DroppsProduct[];
};

type OrderableProductOptions = {
  /** Include bulk products as orderable (default: exclude) */
  bulk?: "exclude" | "include";

  /** point of sale for products (default: storefront) */
  pos?: "storefront" | "portal";
};

export const catalogSelect = {
  /** A blank catalog */
  default: () => ({ all: [] }),

  /** A list of products that have available variants for sale */
  orderable: (catalog: DroppsCatalog, options?: OrderableProductOptions) =>
    catalog.all.flatMap((p) => {
      const orderableProduct = productSelect.orderable(p, options);
      return orderableProduct ? [orderableProduct] : [];
    }),
};

export function parseScent(title: string): string | undefined {
  return title
    .split(",")[1]
    ?.replace(/Bulk Pods/g, "")
    ?.trim();
}

export const shortenTitle = (title: string) =>
  title
    .replace(/laundry & household|pods/gi, "")
    .replace(/laundry detergent/gi, "Detergent")
    .trim();

export const productSelect = {
  defaultVariant: (p: DroppsProduct) =>
    check(p.variants[0], "missing default variant"),

  selectedVariant: (p: DroppsProduct, id: number) =>
    p.variants.find((v) => v.id === id),

  url: (p: { handle: string }) => `/products/${p.handle}`,

  isBulk: (p: DroppsProduct) => p.title.toLocaleLowerCase().includes("bulk"),

  /** Whether a given product is allowed to be purchased in our subscription portal */
  showInPortal: (p: DroppsProduct) => p.tags.includes("show_in_portal"),

  /**
   * The name of the product is the name shared by all the scents of a product.
   * For the product "Stain & Odor Laundry Detergent Pods, Ocean", returns
   * "Stain & Odor Detergent".
   *
   * The reason why this function isn't restricted to a `DroppsProduct`, so
   * that we can reuse it for use cases like products in cart.
   */
  name: (p: { title: string }) => shortenTitle(p.title.split(",")[0]),

  /**
   * Given the product of "Ultra Concentrated Laundry Detergent Starter Kit
   * Stain & Odor, Lavender Eucalyptus", return "Lavender Eucalyptus". Not all
   * products have scents like dryer balls
   */
  scent: (p: { title: string }) => parseScent(p.title),

  /**
   * Returns the product's featured image. By convention, all orderable
   * products have a featured image enforced.
   */
  featuredImage: (p: DroppsProduct) => {
    const media = check(p.media[0], "no media on product");
    return {
      ...media,
      alt: media.alt ?? undefined,
    };
  },

  /** Returns Product Short Text metafield value */
  highlight: (p: DroppsProduct) =>
    p.productShortText ? p.productShortText : "",

  /** Assuming a product has a selling plan, get the default one */
  defaultSellingPlan: (p: DroppsProduct) => {
    const planName = "Delivery every 4 Months";
    return p.sellingPlans.find((s) => s.name === planName) || p.sellingPlans[0];
  },

  /** Find a selling plan that has a given name */
  findSellingPlan: (p: DroppsProduct, plan: DroppsSellingPlan) =>
    p.sellingPlans.find((s) => s.name === plan.name),

  /** Return null if the product is not orderable else included only orderable variants */
  orderable: (
    p: DroppsProduct,
    options?: OrderableProductOptions
  ): DroppsProduct | null => {
    if (p.hidden || p.media.length === 0) {
      return null;
    }

    if (options?.bulk !== "include" && productSelect.isBulk(p)) {
      return null;
    }

    if (options?.pos === "portal" && !productSelect.showInPortal(p)) {
      return null;
    }

    const variants = p.variants
      .map((v) => ({
        ...v,
        available: v.available && (v.backorderEnabled || !!v.inventoryQuantity),
        subscription: variantSelect.orderableSubscription(v),
      }))
      .filter((v) => !v.hidden && v.available);

    if (variants.length === 0) {
      return null;
    }

    return { ...p, variants };
  },
};

export const variantSelect = {
  /**
   * If the subscription is cheaper, returns the subscription price, else
   * returns the onetime price.
   */
  lowestPrice: (v: DroppsVariant) =>
    variantSelect.orderableSubscription(v)?.discount.price ?? v.price,

  /** Returns if the subscription discount as a percentage */
  subscriptionDiscount: (v: DroppsVariant) =>
    Math.round(
      (1 - variantSelect.lowestPrice(v).asCents() / v.price.asCents()) * 100
    ),

  /**
   * Returns a formatted string that contains the variant's size value (eg: 64)
   * and unit (eg: "count") for user viewing. Optional quantity formatting.
   */
  sizeFormat: (v: { size: DroppsVariant["size"] }, qty: number = 1) => {
    const val = +v.size.value;
    const fallback = qty != 1 ? `${qty} ${v.size.value}` : v.size.value;
    return `${!isNaN(val) ? val * qty : fallback} ${v.size.unit || ""}`.trim();
  },

  /** artificially differentiate synthetic variants in react lists */
  reactKey: (v: DroppsVariant) => v.id,

  /** Return the subscription variant id (if one exists) */
  subscriptionVariantId: (v: DroppsVariant) =>
    v.subscription?.kind === "variant" ? v.subscription.variantId : v.id,

  /** Returns the orderable subscription */
  orderableSubscription: (v: DroppsVariant) =>
    v.subscription?.hidden ? undefined : v.subscription,

  /** Convert variant into a line item to be added to cart */
  cartAddLineItem: (
    variant: DroppsVariant,
    options: {
      subscription?: DroppsSellingPlan | false;
      quantity?: number;
    }
  ): CartAddLineItem => {
    const quantity = options.quantity ?? 1;
    let variantId = variant.id;
    let sellingPlan: undefined | number = undefined;
    if (options.subscription) {
      sellingPlan = options.subscription.id;
      variantId = variantSelect.subscriptionVariantId(variant);
    }

    return {
      id: variantId,
      quantity,
      selling_plan: sellingPlan,
    };
  },
};

export type ShopifyProductVariant = {
  available: boolean;
  barcode: string | null;
  compare_at_price: Money | null;
  hidden: boolean;
  id: number;
  inventory_management?: string;
  inventory_policy?: string;
  inventory_quantity?: number;
  options: string[];
  price: Money;
  requires_shipping: boolean;
  sku: string | null;
  taxable?: boolean;
  title: string;
  weight: Mass;
  package_quantity: number;
  variant_multiplier: number;
  quantity_discount?: number;
  metafields?: any;
};

export type ShopifyProduct = {
  badge?: string;
  bundledItems?: BundledItems;
  descriptionHtml: string;
  productShortText: string;
  featuredQuote?: string;
  relatedProducts?: string[];
  handle: string;
  hidden: boolean;
  id: number;
  media: ShopifyMedia[];
  options: string[];
  sellingPlans: ShopifySellingPlan[];
  tags: string[];
  title: string;
  type: string;
  variants: ShopifyProductVariant[];
  hideSubscription: boolean;
  overrideFutureShipments?: string;
  pdpIngredientSection?: PdpIngredientSection | undefined;
  pdpTabData?: PdpTabData | undefined;
  variantMetadata?: Map<number, number>;
  scent?: ScentDetails;
  productGroup?: string;
  productCardDescription?: string;
};

export type ShopifySellingPlan = {
  id: number;
  name: string;
  discount: DroppsDiscountFormula;
};
